// IE Hub - Notes Dialog Component
// Dialog for adding explanatory notes to form fields
// Mobile: bottom slide, full page | Desktop: right slide, partial

import React, { useState, useEffect } from 'react';
import {
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
  UIButton as Button,
  TextField,
} from '../ui';
import { TransitionProps } from '@mui/material/transitions';
import { useAlert } from '@/app/providers/AlertProvider';
import { enforceLimit } from '@/shared/lib/inputLimit';

const MAX_NOTE_LENGTH = 200;

interface NotesDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (noteText: string) => void;
  fieldLabel: string;
  initialValue?: string;
  zIndex?: number;
  // Optional: instead of simple textarea, render a JSON-driven Screen inside the dialog
  embeddedSchema?: any;
  onEmbeddedSubmit?: (data: Record<string, unknown>) => void;
}

// Slide transition for mobile (from bottom)
const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} appear={props.appear ?? true} />;
});

export default function NotesDialog({
  open,
  onClose,
  onSave,
  fieldLabel,
  initialValue = '',
  zIndex = 1303,
  embeddedSchema,
  onEmbeddedSubmit,
}: NotesDialogProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showWarning } = useAlert();
  const [noteText, setNoteText] = useState(initialValue);

  // Update local state when initialValue changes
  useEffect(() => {
    if (open) {
      setNoteText(initialValue);
    }
  }, [open, initialValue]);

  const handleSave = () => {
    onSave(noteText);
    onClose();
  };

  const handleCancel = () => {
    setNoteText(initialValue); // Reset to initial value
    onClose();
  };

  const dialogContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: '64px',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Add a Note
        </Typography>
        <IconButton
          onClick={handleCancel}
          sx={{
            p: 1,
            backgroundColor: 'action.hover',
            '&:hover': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          overflow: 'auto',
        }}
      >
        {embeddedSchema ? (
          // Render embedded JSON-driven screen with only a Save footer handled below
          // We import Screen dynamically at call sites where we pass embeddedSchema
          // so here we just display helper text; actual embedding is done by parent or via portal.
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Embedded form is loading...
          </Typography>
        ) : (
          <>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
              Please provide additional information for <strong>{fieldLabel}</strong>. This note
              will help creditors better understand your situation.
            </Typography>

            <TextField
              multiline
              rows={6}
              maxRows={12}
              fullWidth
              value={noteText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNoteText(enforceLimit(e.target.value, MAX_NOTE_LENGTH, 'Note', showWarning))
              }
              placeholder="Type your explanation here..."
              inputProps={{ maxLength: MAX_NOTE_LENGTH }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Footer with action buttons */}
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            minWidth: '100px',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (embeddedSchema && onEmbeddedSubmit) {
              onEmbeddedSubmit({});
            } else {
              handleSave();
            }
          }}
          sx={{
            minWidth: '100px',
          }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );

  if (isMobile) {
    // Mobile: Full-screen dialog sliding from bottom
    return (
      <Dialog
        open={open}
        onClose={handleCancel}
        TransitionComponent={SlideUpTransition}
        fullScreen
        sx={{
          zIndex: zIndex,
          '& .MuiDialog-paper': {
            backgroundColor: 'background.default',
            backgroundImage: 'none',
            margin: 0,
            borderRadius: '16px 16px 0 0',
          },
        }}
      >
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {/* Swipe Indicator */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: 1,
              pb: 0.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 4,
                backgroundColor: 'action.disabled',
                borderRadius: 2,
                opacity: 0.6,
              }}
            />
          </Box>
          {dialogContent}
        </DialogContent>
      </Dialog>
    );
  }

  // Desktop: Drawer sliding from right
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCancel}
      sx={{
        zIndex: zIndex,
        '& .MuiDrawer-paper': {
          width: { md: '400px', lg: '480px' },
          maxWidth: '40vw',
          backgroundColor: 'background.paper',
          border: 'none',
          boxShadow: theme.shadows[8],
          borderRadius: '16px 0 0 16px',
        },
      }}
    >
      {dialogContent}
    </Drawer>
  );
}

